/**
 * Community Garden — Onboarding State Management
 * 
 * GARDEN-136: Dedicated module for onboarding persistence and resumability.
 * Handles saving/loading progress, completed steps tracking, and settings integration.
 */

import type { OnboardingStep } from './components/Onboarding.js';

export const STORAGE_KEY = 'garden_onboarding_completed';
export const PROGRESS_KEY = 'garden_onboarding_progress';
export const COMPLETED_STEPS_KEY = 'garden_onboarding_completed_steps';
export const SKIPPED_KEY = 'garden_onboarding_skipped';
export const SETTINGS_KEY = 'garden_settings';
export const STORAGE_VERSION = '1';

// REC-006: Session expiry time (24 hours in milliseconds)
export const SESSION_EXPIRY_MS = 24 * 60 * 60 * 1000;

export interface OnboardingProgress {
  version: string;
  currentStepIndex: number;
  lastUpdated: string;
  stepIdsCompleted: string[];
  totalSteps: number;
}

export interface OnboardingSettings {
  onboardingResetAvailable: boolean;
  lastResetAt: string | null;
}

export interface OnboardingStateSnapshot {
  hasCompleted: boolean;
  hasProgress: boolean;
  isSkipped: boolean;
  currentStepIndex: number;
  completedSteps: string[];
  totalSteps: number;
  canResume: boolean;
  lastUpdated: string | null;
  // REC-006: Session expiry info
  isSessionExpired: boolean;
  sessionTimeRemaining: number;
}

/* ── Progress Persistence ─────────────────────────────── */

/**
 * Save current onboarding progress to localStorage
 */
export function saveProgress(
  currentStepIndex: number,
  stepIdsCompleted: string[],
  totalSteps: number
): void {
  try {
    const progress: OnboardingProgress = {
      version: STORAGE_VERSION,
      currentStepIndex,
      lastUpdated: new Date().toISOString(),
      stepIdsCompleted,
      totalSteps
    };
    localStorage.setItem(PROGRESS_KEY, JSON.stringify(progress));
    localStorage.setItem(COMPLETED_STEPS_KEY, JSON.stringify(stepIdsCompleted));
  } catch (e) {
    console.warn('[onboarding-state] Failed to save progress:', e);
  }
}

/**
 * Load saved progress from localStorage
 */
export function loadProgress(): OnboardingProgress | null {
  try {
    const data = localStorage.getItem(PROGRESS_KEY);
    if (!data) return null;
    const parsed = JSON.parse(data) as OnboardingProgress;
    if (parsed.version !== STORAGE_VERSION) return null;
    return parsed;
  } catch (e) {
    console.warn('[onboarding-state] Failed to load progress:', e);
    return null;
  }
}

/**
 * Clear saved progress (but keep completion status)
 */
export function clearProgress(): void {
  try {
    localStorage.removeItem(PROGRESS_KEY);
    localStorage.removeItem(SKIPPED_KEY);
  } catch (e) {
    console.warn('[onboarding-state] Failed to clear progress:', e);
  }
}

/* ── Completed Steps Tracking ─────────────────────────── */

/**
 * Mark a specific step as completed
 */
export function markStepCompleted(stepId: string): void {
  try {
    const completed = getCompletedSteps();
    if (!completed.includes(stepId)) {
      completed.push(stepId);
      localStorage.setItem(COMPLETED_STEPS_KEY, JSON.stringify(completed));
    }
  } catch (e) {
    console.warn('[onboarding-state] Failed to mark step completed:', e);
  }
}

/**
 * Get list of all completed step IDs
 */
export function getCompletedSteps(): string[] {
  try {
    const data = localStorage.getItem(COMPLETED_STEPS_KEY);
    if (!data) return [];
    return JSON.parse(data);
  } catch (e) {
    console.warn('[onboarding-state] Failed to get completed steps:', e);
    return [];
  }
}

/**
 * Check if a specific step has been completed
 */
export function isStepCompleted(stepId: string): boolean {
  return getCompletedSteps().includes(stepId);
}

/**
 * Clear completed steps tracking
 */
export function clearCompletedSteps(): void {
  try {
    localStorage.removeItem(COMPLETED_STEPS_KEY);
  } catch (e) {
    console.warn('[onboarding-state] Failed to clear completed steps:', e);
  }
}

/* ── Completion Status ────────────────────────────────── */

/**
 * Mark onboarding as fully completed
 */
export function markCompleted(): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({
      version: STORAGE_VERSION,
      completed: true,
      completedAt: new Date().toISOString()
    }));
    // Clear in-progress progress since we're done
    clearProgress();
  } catch (e) {
    console.warn('[onboarding-state] Failed to mark completed:', e);
  }
}

/**
 * Check if onboarding has been completed
 */
export function hasCompleted(): boolean {
  try {
    const data = localStorage.getItem(STORAGE_KEY);
    if (!data) return false;
    const parsed = JSON.parse(data);
    return parsed.version === STORAGE_VERSION && parsed.completed === true;
  } catch (e) {
    console.warn('[onboarding-state] Failed to check completion:', e);
    return false;
  }
}

/**
 * Get completion timestamp
 */
export function getCompletionDate(): string | null {
  try {
    const data = localStorage.getItem(STORAGE_KEY);
    if (!data) return null;
    const parsed = JSON.parse(data);
    return parsed.completedAt || null;
  } catch (e) {
    return null;
  }
}

/* ── Skip & Resume ────────────────────────────────────── */

/**
 * Mark onboarding as skipped (can resume later)
 */
export function markSkipped(): void {
  try {
    localStorage.setItem(SKIPPED_KEY, JSON.stringify({
      version: STORAGE_VERSION,
      skipped: true,
      skippedAt: new Date().toISOString()
    }));
  } catch (e) {
    console.warn('[onboarding-state] Failed to mark skipped:', e);
  }
}

/**
 * Check if onboarding was skipped
 */
export function wasSkipped(): boolean {
  try {
    const data = localStorage.getItem(SKIPPED_KEY);
    if (!data) return false;
    const parsed = JSON.parse(data);
    return parsed.version === STORAGE_VERSION && parsed.skipped === true;
  } catch (e) {
    return false;
  }
}

/**
 * Clear skipped status (when resuming)
 */
export function clearSkipped(): void {
  try {
    localStorage.removeItem(SKIPPED_KEY);
  } catch (e) {
    console.warn('[onboarding-state] Failed to clear skipped status:', e);
  }
}

/**
 * Check if onboarding can be resumed
 */
export function canResume(): boolean {
  const progress = loadProgress();
  if (!progress) return false;
  if (hasCompleted()) return false;
  
  // REC-006: Check for session expiry
  if (isSessionExpired()) {
    return false;
  }
  
  return true;
}

/**
 * REC-006: Check if the onboarding session has expired
 * Returns true if last activity was more than SESSION_EXPIRY_MS ago
 */
export function isSessionExpired(): boolean {
  const progress = loadProgress();
  if (!progress?.lastUpdated) return false;
  
  const lastUpdated = new Date(progress.lastUpdated).getTime();
  const now = Date.now();
  return (now - lastUpdated) > SESSION_EXPIRY_MS;
}

/**
 * REC-006: Get time remaining before session expires (in ms)
 * Returns 0 if expired or no progress
 */
export function getSessionTimeRemaining(): number {
  const progress = loadProgress();
  if (!progress?.lastUpdated) return 0;
  
  const lastUpdated = new Date(progress.lastUpdated).getTime();
  const expiryTime = lastUpdated + SESSION_EXPIRY_MS;
  const remaining = expiryTime - Date.now();
  
  return Math.max(0, remaining);
}

/**
 * Get the step index to resume from
 */
export function getResumeStepIndex(): number {
  const progress = loadProgress();
  if (!progress) return 0;
  
  // Resume from first uncompleted step
  const completed = new Set(progress.stepIdsCompleted);
  for (let i = 0; i < progress.totalSteps; i++) {
    const stepId = `step-${i}`; // Fallback
    if (!completed.has(stepId)) {
      return i;
    }
  }
  return progress.currentStepIndex;
}

/* ── Settings Integration ─────────────────────────────── */

/**
 * Save onboarding settings
 */
export function saveSettings(settings: Partial<OnboardingSettings>): void {
  try {
    const current = loadSettings();
    const updated = { ...current, ...settings };
    localStorage.setItem(SETTINGS_KEY, JSON.stringify(updated));
  } catch (e) {
    console.warn('[onboarding-state] Failed to save settings:', e);
  }
}

/**
 * Load onboarding settings
 */
export function loadSettings(): OnboardingSettings {
  try {
    const data = localStorage.getItem(SETTINGS_KEY);
    const defaults: OnboardingSettings = {
      onboardingResetAvailable: true,
      lastResetAt: null
    };
    if (!data) return defaults;
    return { ...defaults, ...JSON.parse(data) };
  } catch (e) {
    console.warn('[onboarding-state] Failed to load settings:', e);
    return {
      onboardingResetAvailable: true,
      lastResetAt: null
    };
  }
}

/* ── Reset Functionality ──────────────────────────────── */

/**
 * Reset all onboarding state (for settings "Start Over" option)
 */
export function resetAll(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(PROGRESS_KEY);
    localStorage.removeItem(COMPLETED_STEPS_KEY);
    localStorage.removeItem(SKIPPED_KEY);
    
    // Update settings with reset timestamp
    saveSettings({
      lastResetAt: new Date().toISOString()
    });
    
    console.log('[onboarding-state] All onboarding state reset');
  } catch (e) {
    console.warn('[onboarding-state] Failed to reset:', e);
  }
}

/**
 * Soft reset - clear progress but keep completion status
 */
export function resetProgressOnly(): void {
  clearProgress();
  clearCompletedSteps();
  clearSkipped();
}

/* ── State Snapshot ──────────────────────────────────── */

/**
 * Get complete onboarding state snapshot
 */
export function getStateSnapshot(totalSteps: number = 11): OnboardingStateSnapshot {
  const progress = loadProgress();
  const completedSteps = getCompletedSteps();
  
  return {
    hasCompleted: hasCompleted(),
    hasProgress: !!progress,
    isSkipped: wasSkipped(),
    currentStepIndex: progress?.currentStepIndex ?? 0,
    completedSteps,
    totalSteps: progress?.totalSteps ?? totalSteps,
    canResume: canResume(),
    lastUpdated: progress?.lastUpdated ?? null,
    // REC-006: Session expiry info
    isSessionExpired: isSessionExpired(),
    sessionTimeRemaining: getSessionTimeRemaining()
  };
}

/* ── App Load Integration ─────────────────────────────── */

/**
 * Check if onboarding should auto-start on app load
 */
export function shouldAutoStart(): boolean {
  if (hasCompleted()) return false;
  if (wasSkipped()) return false;
  
  const progress = loadProgress();
  if (!progress) return true; // No progress = first time
  
  // REC-006: Check if session expired - restart from beginning
  if (isSessionExpired()) {
    clearProgress();
    clearCompletedSteps();
    clearSkipped();
    return true; // Start fresh
  }
  
  // Has progress but not completed = resume
  return true;
}

/**
 * Get initial state for app load
 * Returns the step index to start from, or -1 if should not start
 */
export function getInitialStepIndex(): number {
  if (hasCompleted()) return -1;
  
  const progress = loadProgress();
  if (!progress) return 0; // Start from beginning
  
  // REC-006: If session expired, start from beginning
  if (isSessionExpired()) {
    clearProgress();
    clearCompletedSteps();
    clearSkipped();
    return 0;
  }
  
  // Resume from saved position
  clearSkipped(); // Clear skipped status when actually starting
  return progress.currentStepIndex;
}

/* ── Event Helpers ───────────────────────────────────── */

/**
 * Dispatch onboarding state change event
 */
export function dispatchStateChange(detail: {
  type: 'progress' | 'completed' | 'skipped' | 'reset';
  stepIndex?: number;
  stepId?: string;
}): void {
  window.dispatchEvent(new CustomEvent('onboarding:statechange', { detail }));
}

/**
 * Listen for onboarding state changes
 */
export function onStateChange(
  callback: (event: CustomEvent) => void
): () => void {
  const handler = (e: Event) => callback(e as CustomEvent);
  window.addEventListener('onboarding:statechange', handler);
  return () => window.removeEventListener('onboarding:statechange', handler);
}
